import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    Bot,
    Calendar,
    Car,
    CheckCircle2,
    Download,
    ExternalLink,
    FileCheck2,
    FileText,
    Loader2,
    Package,
    RefreshCw,
    Scale,
    Sparkles,
    XCircle,
} from "lucide-react";
import { AiMarkdown } from "@/components/ai/AiMarkdown";
import { absoluteUrl, triggerDownload } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge, PanelCard } from "./ui";

const SUB_TABS = [
    { id: "genel", label: "Genel bakış", icon: Scale },
    { id: "dilekce", label: "STK dilekçe", icon: FileText },
    { id: "checklist", label: "Checklist & paket", icon: FileCheck2 },
    { id: "akil", label: "Akıl araçları", icon: Bot },
];

function InfoGrid({ title, icon: Icon, rows, accent = "gold" }) {
    const border =
        accent === "brand"
            ? "border-[#8b1f2f]/30 bg-[#8b1f2f]/5"
            : accent === "cyan"
              ? "border-sky-500/25 bg-sky-500/5"
              : "border-[#D4AF37]/25 bg-[#D4AF37]/5";
    return (
        <div className={`rounded-xl border p-4 ${border}`}>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-[#F3E5AB] mb-3 flex items-center gap-2">
                {Icon ? <Icon className="h-3.5 w-3.5 opacity-80" /> : null}
                {title}
            </h4>
            <dl className="space-y-2">
                {rows.map(([k, v]) => (
                    <div key={k} className="grid grid-cols-[minmax(0,38%)_1fr] gap-2 text-xs">
                        <dt className="text-slate-500">{k}</dt>
                        <dd className="text-slate-200 break-words">{v || "—"}</dd>
                    </div>
                ))}
            </dl>
        </div>
    );
}

function ProgressRing({ percent }) {
    const p = Math.min(100, Math.max(0, Number(percent) || 0));
    const r = 36;
    const c = 2 * Math.PI * r;
    const offset = c - (p / 100) * c;
    return (
        <div className="relative w-24 h-24 shrink-0">
            <svg className="w-full h-full -rotate-90" viewBox="0 0 88 88">
                <circle cx="44" cy="44" r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="6" />
                <circle
                    cx="44"
                    cy="44"
                    r={r}
                    fill="none"
                    stroke={p >= 80 ? "#34d399" : p >= 50 ? "#D4AF37" : "#f87171"}
                    strokeWidth="6"
                    strokeLinecap="round"
                    strokeDasharray={c}
                    strokeDashoffset={offset}
                />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
                <strong className="text-lg text-white tabular-nums">%{p}</strong>
                <span className="text-[9px] text-slate-500 uppercase">hazır</span>
            </div>
        </div>
    );
}

export default function TahkimAkilPanel({
    tahkimDosyalar,
    tahkimDosyaId,
    setTahkimDosyaId,
    tahkimKarsiPlaka,
    setTahkimKarsiPlaka,
    tahkimSigortaBasvuruTarihi,
    setTahkimSigortaBasvuruTarihi,
    tahkimOnizleme,
    tahkimChecklist,
    tahkimBrifing,
    tahkimRedMetin,
    setTahkimRedMetin,
    tahkimRedSonuc,
    tahkimAciklamalar,
    setTahkimAciklamalar,
    tahkimTalepSonucu,
    setTahkimTalepSonucu,
    tahkimPaketler,
    tahkimLoading,
    busyKey,
    loadTahkimDosyalar,
    loadTahkimChecklist,
    handleTahkimBrifing,
    handleTahkimRedAnaliz,
    handleTahkimPaket,
    handleTahkimOnizlemeYenile,
}) {
    const [subTab, setSubTab] = useState("genel");

    const seciliDosya = useMemo(
        () => tahkimDosyalar.find((d) => String(d.dosya_id) === String(tahkimDosyaId)),
        [tahkimDosyalar, tahkimDosyaId],
    );

    const checklistTamam = useMemo(() => {
        const list = tahkimChecklist?.checklist || [];
        return list.filter((i) => i.tamam).length;
    }, [tahkimChecklist]);

    const checklistToplam = tahkimChecklist?.checklist?.length || 0;

    return (
        <div className="space-y-4">
            {/* Hero */}
            <div className="relative overflow-hidden rounded-2xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#121A2F] via-[#0f1628] to-[#1a1020] p-5 md:p-6">
                <div className="pointer-events-none absolute -top-16 -right-10 h-40 w-40 rounded-full bg-[#D4AF37]/15 blur-3xl" />
                <div className="relative flex flex-col lg:flex-row lg:items-start lg:justify-between gap-5">
                    <div className="max-w-2xl">
                        <div className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/10 px-3 py-1 text-[10px] uppercase tracking-[0.18em] text-[#F3E5AB] mb-3">
                            <Sparkles className="h-3.5 w-3.5" />
                            Tahkim Akıl
                        </div>
                        <h2 className="font-display text-xl md:text-2xl text-white flex items-center gap-2">
                            <Scale className="h-6 w-6 text-[#D4AF37]" />
                            STK hazırlık komuta merkezi
                        </h2>
                        <p className="text-sm text-slate-400 mt-2 leading-relaxed">
                            Checklist, dilekçe önizleme, savunma brifingi, red cevabı ve başvuru paketi —
                            tek dosya üzerinden ilerleyin. Şablon:{" "}
                            <code className="text-[#F3E5AB] text-xs">tahkim_sablon_guncel.docx</code>
                        </p>
                    </div>
                    {seciliDosya ? (
                        <div className="flex items-center gap-4 rounded-xl border border-white/10 bg-black/25 px-4 py-3">
                            <ProgressRing percent={seciliDosya.hazir_oran ?? tahkimChecklist?.hazir_oran} />
                            <div className="min-w-0">
                                <p className="text-[10px] uppercase tracking-wider text-slate-500">Seçili dosya</p>
                                <p className="text-sm font-semibold text-white truncate">{seciliDosya.dosya_no}</p>
                                <p className="text-xs text-slate-400 truncate">{seciliDosya.muvekkil}</p>
                                {seciliDosya.paket_sayisi ? (
                                    <Badge className="mt-1.5 border-emerald-500/30 text-emerald-200 bg-emerald-500/10">
                                        {seciliDosya.paket_sayisi} paket
                                    </Badge>
                                ) : null}
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>

            {/* Dosya seçimi toolbar */}
            <PanelCard className="!p-4">
                <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-3 items-end">
                    <label className="text-xs text-slate-500 block md:col-span-2 xl:col-span-1">
                        <span className="flex items-center gap-1 mb-1 text-slate-400">
                            <FileText className="h-3 w-3" /> Sigorta dosyası
                        </span>
                        <select
                            value={tahkimDosyaId}
                            onChange={(e) => setTahkimDosyaId(e.target.value)}
                            className="w-full rounded-lg border border-white/10 bg-[#0B0F19] px-3 py-2.5 text-sm text-white"
                        >
                            <option value="">Dosya seçin…</option>
                            {tahkimDosyalar.map((d) => (
                                <option key={d.dosya_id} value={d.dosya_id}>
                                    {d.dosya_no} — {d.muvekkil} (%{d.hazir_oran}
                                    {d.paket_sayisi ? ` · ${d.paket_sayisi} paket` : ""})
                                </option>
                            ))}
                        </select>
                    </label>
                    <label className="text-xs text-slate-500 block">
                        <span className="flex items-center gap-1 mb-1 text-slate-400">
                            <Car className="h-3 w-3" /> Karşı plaka
                        </span>
                        <input
                            value={tahkimKarsiPlaka}
                            onChange={(e) => setTahkimKarsiPlaka(e.target.value)}
                            placeholder="60 AG 347"
                            className="w-full rounded-lg border border-white/10 bg-[#0B0F19] px-3 py-2.5 text-sm text-white"
                        />
                    </label>
                    <label className="text-xs text-slate-500 block">
                        <span className="flex items-center gap-1 mb-1 text-slate-400">
                            <Calendar className="h-3 w-3" /> Sigorta başvuru tarihi
                        </span>
                        <input
                            type="date"
                            value={tahkimSigortaBasvuruTarihi}
                            onChange={(e) => setTahkimSigortaBasvuruTarihi(e.target.value)}
                            className="w-full rounded-lg border border-white/10 bg-[#0B0F19] px-3 py-2.5 text-sm text-white"
                        />
                    </label>
                    <div className="flex flex-wrap items-center gap-2">
                        {tahkimOnizleme?.uyusmazlik?.tahkim_hedef_tarih ? (
                            <div className="text-xs rounded-lg border border-[#D4AF37]/25 bg-[#D4AF37]/10 px-3 py-2 text-slate-300">
                                Hedef:{" "}
                                <strong className="text-[#F3E5AB]">
                                    {tahkimOnizleme.uyusmazlik.tahkim_hedef_tarih}
                                </strong>
                                {tahkimOnizleme.uyusmazlik.tahkim_gun_kaldi != null ? (
                                    <span className="block text-[10px] text-slate-500 mt-0.5">
                                        {tahkimOnizleme.uyusmazlik.tahkim_hazir
                                            ? "Tahkime geçilebilir"
                                            : `${tahkimOnizleme.uyusmazlik.tahkim_gun_kaldi} gün kaldı`}
                                    </span>
                                ) : null}
                            </div>
                        ) : null}
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="border-white/15 h-10"
                            disabled={tahkimLoading}
                            onClick={() => {
                                loadTahkimDosyalar().catch(() => {});
                                if (tahkimDosyaId) loadTahkimChecklist(tahkimDosyaId).catch(() => {});
                            }}
                        >
                            {tahkimLoading ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <>
                                    <RefreshCw className="h-4 w-4 mr-1" /> Yenile
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            </PanelCard>

            {!tahkimDosyaId ? (
                <PanelCard>
                    <div className="py-16 text-center text-slate-500">
                        <Scale className="h-12 w-12 mx-auto mb-3 opacity-30" />
                        <p className="text-sm">Tahkim adayı dosya seçin veya listede dosya yok.</p>
                    </div>
                </PanelCard>
            ) : !tahkimChecklist && tahkimLoading ? (
                <PanelCard>
                    <div className="py-16 flex flex-col items-center text-slate-500">
                        <Loader2 className="h-8 w-8 animate-spin mb-3 text-[#D4AF37]" />
                        <p className="text-sm">Checklist yükleniyor…</p>
                    </div>
                </PanelCard>
            ) : (
                <>
                    {/* Alt sekmeler */}
                    <div className="flex gap-1 overflow-x-auto pb-1 -mx-1 px-1">
                        {SUB_TABS.map((t) => {
                            const Icon = t.icon;
                            return (
                                <button
                                    key={t.id}
                                    type="button"
                                    onClick={() => setSubTab(t.id)}
                                    className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-semibold border transition ${
                                        subTab === t.id
                                            ? "bg-[#D4AF37]/15 border-[#D4AF37]/40 text-[#F3E5AB]"
                                            : "border-white/10 text-slate-400 hover:text-white hover:bg-white/5"
                                    }`}
                                >
                                    <Icon className="h-3.5 w-3.5" />
                                    {t.label}
                                </button>
                            );
                        })}
                    </div>

                    {subTab === "genel" && (
                        <div className="grid md:grid-cols-3 gap-4">
                            <PanelCard className="md:col-span-1">
                                <h3 className="text-sm font-semibold text-white mb-3">Hazırlık durumu</h3>
                                <div className="flex items-center gap-4">
                                    <ProgressRing percent={tahkimChecklist?.hazir_oran} />
                                    <div className="text-xs text-slate-400 space-y-1">
                                        <p>
                                            <strong className="text-white">{checklistTamam}</strong> / {checklistToplam}{" "}
                                            madde tamam
                                        </p>
                                        <p>{tahkimPaketler.length} paket oluşturuldu</p>
                                    </div>
                                </div>
                                {tahkimChecklist?.workflow_errors?.length ? (
                                    <div className="mt-3 text-xs text-rose-300 bg-rose-500/10 border border-rose-500/20 rounded-lg p-2">
                                        {tahkimChecklist.workflow_errors.join(" · ")}
                                    </div>
                                ) : null}
                                <div className="mt-4 flex flex-wrap gap-2">
                                    <Button
                                        type="button"
                                        size="sm"
                                        className="bg-[#D4AF37] text-black hover:bg-[#e8c547]"
                                        disabled={busyKey === "tahkim-brifing"}
                                        onClick={() => {
                                            setSubTab("akil");
                                            handleTahkimBrifing();
                                        }}
                                    >
                                        Savunma brifingi
                                    </Button>
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        className="border-white/15"
                                        disabled={busyKey === "tahkim-paket"}
                                        onClick={handleTahkimPaket}
                                    >
                                        Paket oluştur
                                    </Button>
                                </div>
                            </PanelCard>
                            <PanelCard className="md:col-span-2">
                                <h3 className="text-sm font-semibold text-white mb-3">Hızlı adımlar</h3>
                                <ol className="grid sm:grid-cols-2 gap-2 text-xs">
                                    {[
                                        { n: 1, t: "Dosya & evrak checklist", go: "checklist" },
                                        { n: 2, t: "STK dilekçe önizleme", go: "dilekce" },
                                        { n: 3, t: "Red metni analizi", go: "akil" },
                                        { n: 4, t: "ZIP paket indir", go: "checklist" },
                                    ].map((step) => (
                                        <button
                                            key={step.n}
                                            type="button"
                                            onClick={() => setSubTab(step.go)}
                                            className="flex items-center gap-2 rounded-lg border border-white/10 bg-[#0B0F19]/50 px-3 py-2.5 text-left hover:border-[#D4AF37]/30 hover:bg-[#D4AF37]/5 transition"
                                        >
                                            <span className="w-6 h-6 rounded-full bg-[#D4AF37]/20 text-[#F3E5AB] flex items-center justify-center font-bold text-[10px]">
                                                {step.n}
                                            </span>
                                            <span className="text-slate-300">{step.t}</span>
                                        </button>
                                    ))}
                                </ol>
                            </PanelCard>
                        </div>
                    )}

                    {subTab === "dilekce" && tahkimOnizleme && (
                        <PanelCard className="space-y-4">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                                    <FileText className="h-4 w-4 text-[#D4AF37]" />
                                    STK Tahkim dilekçe önizleme
                                </h3>
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    className="border-white/15"
                                    disabled={busyKey === "tahkim-onizleme"}
                                    onClick={handleTahkimOnizlemeYenile}
                                >
                                    {busyKey === "tahkim-onizleme" ? (
                                        <Loader2 className="h-4 w-4 animate-spin mr-1" />
                                    ) : (
                                        <RefreshCw className="h-4 w-4 mr-1" />
                                    )}
                                    Önizlemeyi güncelle
                                </Button>
                            </div>
                            <div className="grid md:grid-cols-2 gap-3">
                                <InfoGrid
                                    title="A) Başvuru sahibi"
                                    rows={[
                                        ["Ad soyad", tahkimOnizleme.basvuru_sahibi?.ad_soyad],
                                        ["T.C.", tahkimOnizleme.basvuru_sahibi?.tc],
                                        ["Telefon", tahkimOnizleme.basvuru_sahibi?.telefon],
                                        ["E-posta", tahkimOnizleme.basvuru_sahibi?.email],
                                    ]}
                                />
                                <InfoGrid
                                    title="B) Temsilci"
                                    accent="brand"
                                    rows={[
                                        [
                                            "Unvan / ad",
                                            `${tahkimOnizleme.temsilci?.temsilci_unvan || ""} ${tahkimOnizleme.temsilci?.temsilci_ad || ""}`.trim(),
                                        ],
                                        ["T.C.", tahkimOnizleme.temsilci?.temsilci_tc],
                                        ["Telefon", tahkimOnizleme.temsilci?.temsilci_tel],
                                        ["KEP", tahkimOnizleme.temsilci?.temsilci_kep],
                                    ]}
                                />
                                <div className="md:col-span-2">
                                    <InfoGrid
                                        title="C) Uyuşmazlık"
                                        accent="cyan"
                                        icon={Car}
                                        rows={[
                                            ["Sigorta", tahkimOnizleme.uyusmazlik?.sigorta_sirketi],
                                            [
                                                "Poliçe",
                                                `${tahkimOnizleme.uyusmazlik?.police_turu || ""} — ${tahkimOnizleme.uyusmazlik?.police_no || ""}`,
                                            ],
                                            ["Konu", tahkimOnizleme.uyusmazlik?.konu],
                                            [
                                                "Plaka / karşı",
                                                `${tahkimOnizleme.uyusmazlik?.plaka || "—"} / ${tahkimOnizleme.uyusmazlik?.karsi_plaka || "—"}`,
                                            ],
                                            ["Kaza tarihi", tahkimOnizleme.uyusmazlik?.kaza_tarihi],
                                            ["Sigorta başvuru", tahkimOnizleme.uyusmazlik?.sigorta_basvuru_tarihi],
                                        ]}
                                    />
                                </div>
                            </div>
                            <div className="grid lg:grid-cols-2 gap-4">
                                <label className="block">
                                    <span className="text-xs font-semibold text-[#D4AF37] mb-2 block">
                                        Ç) Açıklamalar
                                    </span>
                                    <textarea
                                        value={tahkimAciklamalar}
                                        onChange={(e) => setTahkimAciklamalar(e.target.value)}
                                        rows={8}
                                        className="w-full rounded-xl border border-white/10 bg-[#0B0F19] px-3 py-2.5 text-sm text-white font-mono leading-relaxed"
                                    />
                                </label>
                                <label className="block">
                                    <span className="text-xs font-semibold text-[#D4AF37] mb-2 block">
                                        E) Talep sonucu
                                    </span>
                                    <textarea
                                        value={tahkimTalepSonucu}
                                        onChange={(e) => setTahkimTalepSonucu(e.target.value)}
                                        rows={8}
                                        className="w-full rounded-xl border border-white/10 bg-[#0B0F19] px-3 py-2.5 text-sm text-white font-mono leading-relaxed"
                                    />
                                </label>
                            </div>
                            {tahkimOnizleme.sablon_uyari ? (
                                <p className="text-xs text-amber-300 rounded-lg border border-amber-500/25 bg-amber-500/10 px-3 py-2">
                                    {tahkimOnizleme.sablon_uyari}
                                </p>
                            ) : null}
                        </PanelCard>
                    )}

                    {subTab === "dilekce" && !tahkimOnizleme && (
                        <PanelCard>
                            <p className="text-sm text-slate-500 py-8 text-center">Dilekçe önizlemesi yükleniyor…</p>
                        </PanelCard>
                    )}

                    {subTab === "checklist" && tahkimChecklist && (
                        <div className="grid lg:grid-cols-2 gap-4">
                            <PanelCard className="space-y-4">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                                        <FileCheck2 className="h-4 w-4 text-emerald-400" />
                                        Tahkim hazırlık checklist
                                    </h3>
                                    <Badge className="border-[#D4AF37]/40 text-[#F3E5AB] bg-[#D4AF37]/10">
                                        %{tahkimChecklist.hazir_oran} hazır
                                    </Badge>
                                </div>
                                <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                                    <span
                                        className="block h-full rounded-full bg-gradient-to-r from-[#8b1f2f] via-[#D4AF37] to-emerald-500 transition-all"
                                        style={{ width: `${tahkimChecklist.hazir_oran || 0}%` }}
                                    />
                                </div>
                                <ul className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
                                    {tahkimChecklist.checklist?.map((item) => (
                                        <li
                                            key={item.kod}
                                            className={`flex items-start gap-2 rounded-lg px-2.5 py-2 text-xs ${
                                                item.tamam
                                                    ? "bg-emerald-500/10 text-emerald-100"
                                                    : "bg-rose-500/5 text-slate-300"
                                            }`}
                                        >
                                            {item.tamam ? (
                                                <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
                                            ) : (
                                                <XCircle className="h-4 w-4 text-rose-400 shrink-0 mt-0.5" />
                                            )}
                                            {item.label}
                                        </li>
                                    ))}
                                </ul>
                                <p className="text-[10px] text-slate-500">{tahkimChecklist.stk_bilgi}</p>
                                <div className="flex flex-wrap gap-2 pt-1 border-t border-white/10">
                                    <Button
                                        type="button"
                                        size="sm"
                                        disabled={busyKey === "tahkim-brifing"}
                                        onClick={handleTahkimBrifing}
                                        className="bg-[#D4AF37] text-black hover:bg-[#e8c547]"
                                    >
                                        {busyKey === "tahkim-brifing" ? (
                                            <Loader2 className="h-4 w-4 animate-spin mr-1" />
                                        ) : (
                                            <FileText className="h-4 w-4 mr-1" />
                                        )}
                                        Savunma brifingi
                                    </Button>
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        className="border-white/15"
                                        disabled={busyKey === "tahkim-paket"}
                                        onClick={handleTahkimPaket}
                                    >
                                        {busyKey === "tahkim-paket" ? (
                                            <Loader2 className="h-4 w-4 animate-spin mr-1" />
                                        ) : (
                                            <Package className="h-4 w-4 mr-1" />
                                        )}
                                        Paket oluştur (ZIP)
                                    </Button>
                                </div>
                            </PanelCard>

                            <PanelCard className="space-y-3">
                                <div className="flex items-center justify-between gap-2">
                                    <h4 className="text-sm font-semibold text-white">Oluşturulan paketler</h4>
                                    <Badge className="border-white/15 text-slate-400 bg-white/5">
                                        {tahkimPaketler.length} kayıt
                                    </Badge>
                                </div>
                                {tahkimPaketler.length === 0 ? (
                                    <p className="text-xs text-slate-500 py-6 text-center">
                                        Henüz paket yok. ZIP oluşturduğunuzda burada listelenir.
                                    </p>
                                ) : (
                                    <ul className="space-y-2 max-h-80 overflow-y-auto">
                                        {tahkimPaketler.map((p, idx) => (
                                            <li
                                                key={p.id || p.zip_url || idx}
                                                className="rounded-xl border border-white/10 bg-[#0B0F19]/60 p-3"
                                            >
                                                <div className="flex flex-wrap items-center justify-between gap-2">
                                                    <div className="text-xs text-slate-300">
                                                        {p.olusturma_tarihi
                                                            ? new Date(p.olusturma_tarihi).toLocaleString("tr-TR")
                                                            : "—"}
                                                        {p.hazir_oran != null ? (
                                                            <span className="text-slate-500"> · %{p.hazir_oran}</span>
                                                        ) : null}
                                                        {p.evrak_sayisi ? (
                                                            <span className="text-slate-500"> · {p.evrak_sayisi} evrak</span>
                                                        ) : null}
                                                    </div>
                                                    <div className="flex gap-1.5">
                                                        {p.docx_url ? (
                                                            <Button
                                                                type="button"
                                                                size="sm"
                                                                variant="outline"
                                                                className="h-7 text-[10px] border-white/15"
                                                                onClick={() => triggerDownload(absoluteUrl(p.docx_url))}
                                                            >
                                                                DOCX
                                                            </Button>
                                                        ) : null}
                                                        <Button
                                                            type="button"
                                                            size="sm"
                                                            variant="outline"
                                                            className="h-7 text-[10px] border-emerald-500/30 text-emerald-200"
                                                            disabled={p.dosya_mevcut === false}
                                                            onClick={() => triggerDownload(absoluteUrl(p.zip_url))}
                                                        >
                                                            <Download className="h-3 w-3 mr-1" />
                                                            ZIP
                                                        </Button>
                                                    </div>
                                                </div>
                                                {p.dosya_mevcut === false ? (
                                                    <p className="text-[10px] text-rose-300 mt-1">Dosya diskte bulunamadı</p>
                                                ) : null}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {tahkimPaketler[0]?.panel_tahkim_href ? (
                                    <div className="flex flex-wrap gap-2 pt-2 border-t border-white/10">
                                        <Link
                                            to={tahkimPaketler[0].panel_tahkim_href}
                                            className="inline-flex items-center text-xs px-3 py-2 rounded-lg border border-white/10 text-[#D4AF37] hover:bg-[#D4AF37]/10"
                                        >
                                            Tahkim dosyası →
                                        </Link>
                                        <a
                                            href={
                                                tahkimPaketler[0].stk_portal_url ||
                                                "https://www.sigortatahkim.org.tr/"
                                            }
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded-lg border border-white/10 text-slate-300 hover:bg-white/5"
                                        >
                                            <ExternalLink className="h-3.5 w-3.5" /> STK portal
                                        </a>
                                    </div>
                                ) : null}
                            </PanelCard>
                        </div>
                    )}

                    {subTab === "akil" && (
                        <div className="grid lg:grid-cols-2 gap-4">
                            <PanelCard className="space-y-3">
                                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                                    <Bot className="h-4 w-4 text-[#8b1f2f]" />
                                    Sigorta red → cevap taslağı
                                </h3>
                                <Textarea
                                    value={tahkimRedMetin}
                                    onChange={(e) => setTahkimRedMetin(e.target.value)}
                                    placeholder="Sigorta red / eksik ödeme yazısını buraya yapıştırın…"
                                    rows={6}
                                    className="bg-[#0B0F19] border-white/10 text-white text-sm"
                                />
                                <Button
                                    type="button"
                                    size="sm"
                                    disabled={busyKey === "tahkim-red"}
                                    onClick={handleTahkimRedAnaliz}
                                    className="bg-[#8b1f2f] text-white hover:bg-[#a02538]"
                                >
                                    {busyKey === "tahkim-red" ? (
                                        <Loader2 className="h-4 w-4 animate-spin mr-1" />
                                    ) : null}
                                    Analiz et & taslak üret
                                </Button>
                                {tahkimRedSonuc ? (
                                    <div className="rounded-xl border border-white/10 bg-[#0B0F19]/80 p-4 text-sm max-h-64 overflow-y-auto">
                                        <AiMarkdown text={tahkimRedSonuc} />
                                    </div>
                                ) : (
                                    <p className="text-xs text-slate-500">Red metnini yapıştırıp analiz başlatın.</p>
                                )}
                            </PanelCard>

                            <PanelCard className="space-y-3">
                                <div className="flex items-center justify-between gap-2">
                                    <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                                        <Sparkles className="h-4 w-4 text-[#D4AF37]" />
                                        Savunma stratejisi brifingi
                                    </h3>
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        className="border-white/15 h-8 text-xs"
                                        disabled={busyKey === "tahkim-brifing"}
                                        onClick={handleTahkimBrifing}
                                    >
                                        {busyKey === "tahkim-brifing" ? (
                                            <Loader2 className="h-3 w-3 animate-spin" />
                                        ) : (
                                            "Üret"
                                        )}
                                    </Button>
                                </div>
                                {tahkimBrifing ? (
                                    <div className="rounded-xl border border-[#D4AF37]/20 bg-[#0B0F19]/50 p-4 text-sm max-h-96 overflow-y-auto">
                                        <AiMarkdown text={tahkimBrifing} />
                                    </div>
                                ) : (
                                    <div className="py-12 text-center text-slate-500 text-sm">
                                        <Bot className="h-10 w-10 mx-auto mb-2 opacity-30" />
                                        Brifing henüz üretilmedi.
                                    </div>
                                )}
                            </PanelCard>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
